import { format, parse, parseJSON } from 'date-fns';

/**
 * Responsible for handling the conversion of Date only (not DateTime) values.
 * The reason for this is that SAP throws away the time and timezone component resulting in an incorrect output date.
 *
 * @example
 * The SAP backend expects this format `/Date(Unix Millisecond Timestamp)/`
 * So the date is converted to Unix time, which always is in UTC.
 * However due to our local timezone when converting the date to UTC makes it change the day to yesterday.
 * Now Sap receives this UNIX timestamp and throws away the time and timezone,
 * leaving it only with the date which is yesterday.
 *
 * Example:
 * Today@01:00 converted to UTC becomes Yesterday@23:00
 * SAP receives Yesterday@23:00 and throws away @23:00 leaving it with only Yesterday
 * When we ask SAP for this date again it'll send Yesterday@00:00 and we convert that to local time which is Yesterday@02:00
 * Today@01:00 !== Yesterday@02:00 and everything explodes! 💥😭
 *
 * The solution to this odd behavior is to only rely on the Date part and throw away the time and timezone.
 * Meaning that when converting Today@??:?? to UTC it'll become Today@00:00
 */
export const convertToSapDate = (date: Date | null | undefined): string | null | undefined => {
  // eslint-disable-next-line no-null/no-null
  if (date === null || date === undefined) {
    return date;
  }

  // Gets the local date as string with no time component
  const localDateString = format(date, 'yyyy-MM-dd');
  // Creates utc date object to strip local timezone
  const utcDate = parseJSON(`${localDateString}T00:00:00Z`);

  // Creates SAP date string from date
  return format(utcDate, "'/Date('T')/'");
};

export const convertFromSapDate = (sapDate?: Date | string | null): Date | null | undefined => {
  // eslint-disable-next-line no-null/no-null
  if (sapDate === null || sapDate === undefined) {
    return sapDate;
  }

  if (typeof sapDate === 'string' && sapDate.trim() === '') {
    return undefined;
  }

  const localDate = sapDate instanceof Date ? sapDate : parse(sapDate, "'/Date('T')/'", new Date());

  // Gets the utc date as string with no time component
  let isoDateString: string;

  try {
    isoDateString = localDate.toISOString();
  } catch (error) {
    if (error instanceof Error) {
      console.error(error);
    }

    return undefined;
  }

  const utcDateString = isoDateString.substring(0, 10);

  // Creates local date from date string
  return parse(utcDateString, 'yyyy-MM-dd', new Date());
};
